<template>
    <Teleport to="body">
        <div class="template-preview" role="dialog" aria-modal="true" :aria-label="heading">
            <header class="preview-header">
                <button class="back" title="Close" aria-label="Close" @click="emit('close')">←</button>
                <span class="icon sheet-icon"></span>
                <div class="title-block">
                    <div class="title-row">
                        <span class="title">{{ heading }}</span>
                        <span class="badge">Read-only</span>
                    </div>
                    <div v-if="data?.filename" class="subline">{{ data.filename }}</div>
                </div>
                <div class="header-actions">
                    <button v-if="canDownload" class="btn primary" :disabled="downloading || !data" @click="download()">
                        <LoaderItem v-if="downloading" size="14px" />Download .xlsx
                    </button>
                    <button class="btn" @click="emit('close')">Close</button>
                </div>
            </header>

            <div v-if="loading" class="state"><LoaderItem size="24px" /></div>
            <div v-else-if="!data" class="state">The template could not be opened.</div>
            <div v-else-if="!sheets.length" class="state">This template file has no content.</div>
            <template v-else>
                <div v-if="active?.truncated" class="banner">
                    Only the first part of this sheet is shown; download the file to see all of it.
                </div>
                <div class="sheet-holder">
                    <table class="sheet" v-if="active">
                        <colgroup>
                            <col class="row-number-col">
                            <col v-for="(width, x) in colWidths" :key="x" :style="{ width: `${width}px` }">
                        </colgroup>
                        <thead>
                            <tr>
                                <th class="corner"></th>
                                <th v-for="(_, x) in colWidths" :key="x">{{ columnName(x) }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, y) in rows" :key="y">
                                <th class="row-number">{{ y + 1 }}</th>
                                <template v-for="(cell, x) in row" :key="x">
                                    <td
                                        v-if="!covered.has(`${y}:${x}`)"
                                        :rowspan="spans.get(`${y}:${x}`)?.rs || 1"
                                        :colspan="spans.get(`${y}:${x}`)?.cs || 1"
                                        :class="{ bold: cell.b, italic: cell.i, wrap: cell.w }"
                                        :style="cellStyle(cell)"
                                        :title="displayValue(cell.v)"
                                    >{{ displayValue(cell.v) }}</td>
                                </template>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="sheets.length > 1" class="tabs">
                    <button
                        v-for="(sheet, i) in sheets" :key="i"
                        class="tab" :class="{ active: active === sheet }"
                        @click="active = sheet"
                    >{{ sheet.name.trim() }}</button>
                </div>
            </template>
        </div>
    </Teleport>
</template>

<script setup>
    import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
    import API from '@/api/index.js';
    import LoaderItem from '@/components/LoaderItem.vue';
    import { formatIsoDate } from '@/utils/date_display.js';

    const props = defineProps({
        type: { type: String, required: true },     // FIU template type
        title: { type: String, default: '' },
        canDownload: { type: Boolean, default: true },
    });
    const emit = defineEmits(['close']);

    const loading = ref(true);
    const downloading = ref(false);
    const data = ref(null);
    const active = ref(null);

    const heading = computed(() => data.value?.label || props.title || 'Template');

    // Only the visible sheets (the server leaves out the hidden system lists)
    const sheets = computed(() => (data.value?.sheets || []).filter(sheet => !sheet.hidden && sheet.rows.length));

    // Like a spreadsheet, the sheet continues past its content: empty rows/columns fill the view
    const MIN_ROWS = 100;
    const MIN_COLS = 26;
    const EMPTY_CELL = Object.freeze({ v: '' });
    // Each column is as wide as its widest cell (merged cells span several columns, so they don't count)
    const CELL_PADDING = 16;   // 6px left/right padding + border + a little room
    const MIN_COL_WIDTH = 40;  // empty columns
    const MAX_COL_WIDTH = 500; // longer text is cut with "…" (the full text is in the cell's tooltip)
    let measureCtx = null;
    function textWidth(text, bold) {
        if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
        measureCtx.font = `${bold ? '700' : '400'} 13px ${getComputedStyle(document.body).fontFamily}`;
        return measureCtx.measureText(text).width;
    }
    const colWidths = computed(() => {
        const sheet = active.value;
        const count = Math.max(MIN_COLS, sheet?.columnCount || 0);
        const spanning = new Set((sheet?.merges || []).filter(m => m.cs > 1).map(m => `${m.r}:${m.c}`));
        const widths = Array.from({ length: count }, (_, x) => Math.max(MIN_COL_WIDTH, textWidth(columnName(x), false) + CELL_PADDING));
        (sheet?.rows || []).forEach((row, y) => row.forEach((cell, x) => {
            const text = displayValue(cell.v);
            if (!text || spanning.has(`${y}:${x}`)) return;
            widths[x] = Math.max(widths[x], textWidth(text, !!cell.b) + CELL_PADDING);
        }));
        // A merged cell wider than the columns it spans widens them evenly, just enough to fit
        (sheet?.merges || []).forEach(m => {
            const cell = sheet.rows[m.r]?.[m.c];
            const text = cell && displayValue(cell.v);
            if (m.cs < 2 || !text) return;
            const xs = Array.from({ length: m.cs }, (_, i) => m.c + i).filter(x => x < count);
            const missing = textWidth(text, !!cell.b) + CELL_PADDING - xs.reduce((sum, x) => sum + widths[x], 0);
            if (missing > 0) xs.forEach(x => { widths[x] += missing / xs.length; });
        });
        return widths.map(width => Math.ceil(Math.min(MAX_COL_WIDTH, width)));
    });
    const rows = computed(() => {
        const width = colWidths.value.length;
        const result = (active.value?.rows || []).map(row => row.length < width
            ? row.concat(Array(width - row.length).fill(EMPTY_CELL))
            : row);
        while (result.length < MIN_ROWS) result.push(Array(width).fill(EMPTY_CELL));
        return result;
    });

    // Merged ranges: the top-left cell spans, the others are skipped
    const spans = computed(() => new Map((active.value?.merges || []).map(m => [`${m.r}:${m.c}`, m])));
    const covered = computed(() => {
        const set = new Set();
        (active.value?.merges || []).forEach(m => {
            for (let y = m.r; y < m.r + m.rs; y++) {
                for (let x = m.c; x < m.c + m.cs; x++) {
                    if (y !== m.r || x !== m.c) set.add(`${y}:${x}`);
                }
            }
        });
        return set;
    });

    function columnName(x) {
        let name = '';
        for (let n = x + 1; n > 0; n = Math.floor((n - 1) / 26)) {
            name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
        }
        return name;
    }

    // Dates come as ISO text: show them in the browser's date format, like the grid does
    const displayValue = (value) => formatIsoDate(value);

    function cellStyle(cell) {
        const style = {};
        if (cell.bg) style.backgroundColor = cell.bg;
        if (cell.c) style.color = cell.c;
        if (cell.al) style.textAlign = cell.al;
        return style;
    }

    async function download() {
        downloading.value = true;
        await API.downloadTemplate(props.type, data.value?.filename || `${props.type}.xlsx`);
        downloading.value = false;
    }

    function onKeyDown(event) {
        if (event.key === 'Escape') emit('close');
    }

    let previousBodyOverflow = '';

    onMounted(async () => {
        // The page behind the full-screen view must not scroll
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        const response = await API.previewTemplate(props.type);
        data.value = response || null;
        active.value = sheets.value[0] || null;
        loading.value = false;
    });

    onBeforeUnmount(() => {
        document.body.style.overflow = previousBodyOverflow;
        document.removeEventListener('keydown', onKeyDown);
    });
</script>

<style scoped lang="scss">
// Full screen like the drive's sheet view: covers the app header and sidebar (z-index ~1000)
// but stays under toasts (9999)
.template-preview {
    --muted: #5f6368;
    position: fixed;
    inset: 0;
    z-index: 1500;
    background: var(--app-background, #F6F6FC);
    padding: 10px 16px 12px;
    box-sizing: border-box;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}
.state {
    padding: 60px;
    display: flex;
    justify-content: center;
    color: var(--muted);
}

.preview-header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}
.back {
    font-size: 22px;
    color: var(--muted);
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    cursor: pointer;
    &:hover { background: #eef1f7; }
}
.icon {
    width: 28px;
    height: 28px;
    flex: none;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
}
.sheet-icon {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%230073ef' d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 8h-8v8H9v-8H5V9h4V5h2v4h8v2z'/%3E%3C/svg%3E");
}
.title-block {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}
.title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-bottom: 4px;
}
.title {
    font-size: 18px;
    color: #1f1f1f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.subline {
    font-size: 13px;
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.badge {
    flex: none;
    white-space: nowrap;
    font-size: 12px;
    line-height: 18px;
    background: #e8f0fe;
    color: #1a56c4;
    border-radius: 10px;
    padding: 1px 8px;
}
.header-actions {
    display: flex;
    gap: 8px;
    flex: none;
}
.btn {
    width: auto;
    padding: 7px 14px;
    border-radius: 8px;
    background: white;
    color: #1f1f1f;
    border: 1px solid var(--border-color);
    font-weight: 600;
    gap: 6px;
    &:hover { background: #f1f4fb; }
    &.primary {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: white;
        &:hover { background: #005cc0; }
    }
    &:disabled { background: #ccc; border-color: #ccc; color: #666; }
}
.banner {
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 14px;
    background: #fef7e0;
    border: 1px solid #f9ab00;
}

.sheet-holder {
    flex: 1;
    min-height: 0;
    overflow: auto;
    background: #fff;
    border: 1px solid var(--border-color, #d0d3d9);
    border-radius: 8px;
}
.sheet {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: max-content;
    font-size: 13px;
    text-align: left;
    td, th {
        border-right: 1px solid #e3e5e8;
        border-bottom: 1px solid #e3e5e8;
        padding: 3px 6px;
        height: 20px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    td.bold { font-weight: 700; }
    td.italic { font-style: italic; }
    td.wrap { white-space: pre-wrap; }
    thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: #f3f4f6;
        color: var(--muted);
        font-weight: 400;
        text-align: center;
    }
    .row-number-col { width: 44px; }
    th.row-number {
        position: sticky;
        left: 0;
        z-index: 1;
        background: #f3f4f6;
        color: var(--muted);
        font-weight: 400;
        text-align: center;
    }
    th.corner {
        left: 0;
        z-index: 3;
    }
}

/* Sheet tabs at the bottom, like a spreadsheet (only when the file has several visible sheets) */
.tabs {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}
.tab {
    width: auto;
    border: 1px solid var(--border-color, #d0d3d9);
    background: #fff;
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
    color: #1f1f1f;
    &.active {
        border-color: var(--primary-color);
        background: #e8f0fe;
        color: #1a56c4;
        font-weight: 600;
    }
}
</style>
